import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Dropdown } from 'semantic-ui-react';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from 'chart.js';
import { useSelector } from 'react-redux';

import { PageSection, Subtitle } from 'common/styles/page';
import {
  getCurrentOrganizationId,
  getOrganizations,
  isCurrentUserSuperUser,
} from 'common/selectors/auth';
import {
  ClientsAnalyticsRow,
  useGetClientsAnalyticsQuery,
} from '../task-management/api/taskManagementApi';

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

type FilterValue = number | 'all';

const chartColors = {
  device: ['#3b82f6', '#10b981', '#f59e0b', '#9ca3af'],
  os: ['#2563eb', '#0ea5e9', '#14b8a6', '#22c55e', '#64748b', '#94a3b8'],
  browser: ['#6366f1', '#14b8a6', '#3b82f6', '#f97316', '#94a3b8'],
};

const humanizeDeviceType = (value: string) => {
  if (value === 'desktop') return 'Desktop';
  if (value === 'mobile') return 'Mobile';
  if (value === 'tablet') return 'Tablet';
  return 'Unknown';
};

const toDropdownOptions = (rows: Array<{ key: string; text: string; value: string | number }>) => rows;

const buildHorizontalBarData = (rows: ClientsAnalyticsRow[], colors: string[]) => ({
  labels: rows.map(row => row.label),
  datasets: [
    {
      label: 'Submissions',
      data: rows.map(row => row.count),
      backgroundColor: rows.map((_, idx) => colors[idx % colors.length]),
      borderRadius: 6,
      borderWidth: 0,
    },
  ],
});

const horizontalBarOptions = {
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: 'y' as const,
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: (ctx: any) => {
          const row = ctx.raw;
          return `Submissions: ${row}`;
        },
      },
    },
  },
  scales: {
    x: {
      beginAtZero: true,
      ticks: { precision: 0 },
      grid: { color: '#eef2f7' },
    },
    y: {
      grid: { display: false },
      ticks: {
        font: {
          family: 'Poppins, sans-serif',
          size: 12,
        },
      },
    },
  },
};

const ClientsAnalytics: React.FC = () => {
  const organizations = useSelector(getOrganizations as any) as Array<{ id: number; name: string }>;
  const isSuperUser = useSelector(isCurrentUserSuperUser);
  const currentOrganizationId = useSelector(getCurrentOrganizationId);

  const [selectedOrganizationId, setSelectedOrganizationId] = useState<FilterValue>(
    isSuperUser ? 'all' : currentOrganizationId
  );
  const [selectedSurveyType, setSelectedSurveyType] = useState<'all' | 'pulse' | 'culture'>('all');
  const [selectedDeviceType, setSelectedDeviceType] = useState<string>('all');
  const [selectedOperatingSystem, setSelectedOperatingSystem] = useState<string>('all');
  const [selectedBrowser, setSelectedBrowser] = useState<string>('all');

  const clientsArgs = {
    surveyType: selectedSurveyType,
    deviceType: selectedDeviceType,
    operatingSystem: selectedOperatingSystem,
    browser: selectedBrowser,
    organizationId:
      selectedOrganizationId === 'all' ? undefined : Number(selectedOrganizationId),
    allOrganizations: selectedOrganizationId === 'all' && isSuperUser,
  };

  const {
    data: clientsData,
    isLoading,
    isFetching,
    error,
  } = useGetClientsAnalyticsQuery(clientsArgs);

  const organizationOptions = useMemo(() => {
    const base = isSuperUser
      ? [{ key: 'all', text: 'All Organizations', value: 'all' as const }]
      : [];
    const orgRows = (organizations || []).map(org => ({
      key: String(org.id),
      text: org.name,
      value: org.id,
    }));
    return toDropdownOptions([...base, ...orgRows]);
  }, [isSuperUser, organizations]);

  const surveyTypeOptions = useMemo(
    () =>
      toDropdownOptions([
        { key: 'all', text: 'All Assessments', value: 'all' },
        { key: 'pulse', text: 'Pulse Surveys', value: 'pulse' },
        { key: 'culture', text: 'Culture Surveys', value: 'culture' },
      ]),
    []
  );

  const deviceTypeOptions = useMemo(
    () =>
      toDropdownOptions([
        { key: 'all', text: 'All Device Types', value: 'all' },
        ...((clientsData?.filters.device_types || []).map(deviceType => ({
          key: deviceType,
          text: humanizeDeviceType(deviceType),
          value: deviceType,
        })) as Array<{ key: string; text: string; value: string }>),
      ]),
    [clientsData?.filters.device_types]
  );

  const osOptions = useMemo(
    () =>
      toDropdownOptions([
        { key: 'all', text: 'All Operating Systems', value: 'all' },
        ...((clientsData?.filters.operating_systems || []).map(osName => ({
          key: osName,
          text: osName,
          value: osName,
        })) as Array<{ key: string; text: string; value: string }>),
      ]),
    [clientsData?.filters.operating_systems]
  );

  const browserOptions = useMemo(
    () =>
      toDropdownOptions([
        { key: 'all', text: 'All Browsers', value: 'all' },
        ...((clientsData?.filters.browsers || []).map(browserName => ({
          key: browserName,
          text: browserName,
          value: browserName,
        })) as Array<{ key: string; text: string; value: string }>),
      ]),
    [clientsData?.filters.browsers]
  );

  const deviceRows = clientsData?.charts.device_types || [];
  const osRows = clientsData?.charts.operating_systems || [];
  const browserRows = clientsData?.charts.browsers || [];

  const deviceDoughnutData = {
    labels: deviceRows.map(row => humanizeDeviceType(row.label)),
    datasets: [
      {
        data: deviceRows.map(row => row.count),
        backgroundColor: deviceRows.map((_, idx) => chartColors.device[idx % chartColors.device.length]),
        borderWidth: 1,
        borderColor: '#fff',
      },
    ],
  };

  const deviceDoughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' as const },
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const count = Number(ctx.raw || 0);
            const total = clientsData?.totals.submissions || 1;
            const pct = ((count / total) * 100).toFixed(1);
            return `${ctx.label}: ${count} (${pct}%)`;
          },
        },
      },
    },
  };

  const isBusy = isLoading || isFetching;
  const hasNoData = !isBusy && (clientsData?.totals.submissions || 0) === 0;

  return (
    <PageSection padtop>
      <FilterGrid>
        {isSuperUser && (
          <FilterItem>
            <FilterLabel>Organization</FilterLabel>
            <Dropdown
              fluid
              selection
              options={organizationOptions}
              value={selectedOrganizationId}
              onChange={(_, data) => setSelectedOrganizationId((data.value as FilterValue) || 'all')}
            />
          </FilterItem>
        )}

        <FilterItem>
          <FilterLabel>Survey Type</FilterLabel>
          <Dropdown
            fluid
            selection
            options={surveyTypeOptions}
            value={selectedSurveyType}
            onChange={(_, data) => setSelectedSurveyType((data.value as 'all' | 'pulse' | 'culture') || 'all')}
          />
        </FilterItem>

        <FilterItem>
          <FilterLabel>Device Type</FilterLabel>
          <Dropdown
            fluid
            selection
            options={deviceTypeOptions}
            value={selectedDeviceType}
            onChange={(_, data) => setSelectedDeviceType(String(data.value || 'all'))}
          />
        </FilterItem>

        <FilterItem>
          <FilterLabel>Operating System</FilterLabel>
          <Dropdown
            fluid
            selection
            options={osOptions}
            value={selectedOperatingSystem}
            onChange={(_, data) => setSelectedOperatingSystem(String(data.value || 'all'))}
          />
        </FilterItem>

        <FilterItem>
          <FilterLabel>Browser</FilterLabel>
          <Dropdown
            fluid
            selection
            options={browserOptions}
            value={selectedBrowser}
            onChange={(_, data) => setSelectedBrowser(String(data.value || 'all'))}
          />
        </FilterItem>
      </FilterGrid>

      {isBusy && <StateText>Loading client analytics...</StateText>}
      {error && !isBusy && <StateText>Unable to load client analytics right now.</StateText>}
      {hasNoData && <StateText>No survey submission client data found for the selected filters.</StateText>}

      {!isBusy && !error && !hasNoData && (
        <>
          <MetricGrid>
            <MetricCard>
              <MetricLabel>Total Survey Submissions</MetricLabel>
              <MetricValue>{clientsData?.totals.submissions || 0}</MetricValue>
            </MetricCard>
            <MetricCard>
              <MetricLabel>Mobile Device Usage</MetricLabel>
              <MetricValue>{clientsData?.totals.mobile_percentage || 0}%</MetricValue>
            </MetricCard>
            <MetricCard>
              <MetricLabel>Most Common Operating System</MetricLabel>
              <MetricValue>{clientsData?.totals.most_common_os || 'None'}</MetricValue>
            </MetricCard>
            <MetricCard>
              <MetricLabel>Most Common Browser</MetricLabel>
              <MetricValue>{clientsData?.totals.most_common_browser || 'None'}</MetricValue>
            </MetricCard>
          </MetricGrid>

          <ChartGrid>
            <ChartPanel>
              <ChartTitle>Device Types</ChartTitle>
              <ChartContainer>
                <Doughnut data={deviceDoughnutData} options={deviceDoughnutOptions} />
              </ChartContainer>
              <CategoryList>
                {deviceRows.map(row => (
                  <CategoryButton
                    key={`device-${row.label}`}
                    type="button"
                    onClick={() => setSelectedDeviceType(row.label)}
                  >
                    <span>{humanizeDeviceType(row.label)}</span>
                    <span>{row.count} ({row.percentage}%)</span>
                  </CategoryButton>
                ))}
              </CategoryList>
            </ChartPanel>

            <ChartPanel>
              <ChartTitle>Operating Systems</ChartTitle>
              <BarWrap>
                <Bar
                  data={buildHorizontalBarData(osRows, chartColors.os)}
                  options={horizontalBarOptions}
                />
              </BarWrap>
              <CategoryList>
                {osRows.map(row => (
                  <CategoryButton
                    key={`os-${row.label}`}
                    type="button"
                    onClick={() => setSelectedOperatingSystem(row.label)}
                  >
                    <span>{row.label}</span>
                    <span>{row.count} ({row.percentage}%)</span>
                  </CategoryButton>
                ))}
              </CategoryList>
            </ChartPanel>

            <ChartPanel>
              <ChartTitle>Browsers</ChartTitle>
              <BarWrap>
                <Bar
                  data={buildHorizontalBarData(browserRows, chartColors.browser)}
                  options={horizontalBarOptions}
                />
              </BarWrap>
              <CategoryList>
                {browserRows.map(row => (
                  <CategoryButton
                    key={`browser-${row.label}`}
                    type="button"
                    onClick={() => setSelectedBrowser(row.label)}
                  >
                    <span>{row.label}</span>
                    <span>{row.count} ({row.percentage}%)</span>
                  </CategoryButton>
                ))}
              </CategoryList>
            </ChartPanel>
          </ChartGrid>
        </>
      )}
    </PageSection>
  );
};

export default ClientsAnalytics;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(220px, 1fr));
  gap: 12px;
  margin-top: 12px;
  margin-bottom: 18px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(200px, 1fr));
  }

  @media (max-width: 720px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const FilterItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FilterLabel = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
`;

const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(160px, 1fr));
  gap: 10px;
  margin-bottom: 18px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 680px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const MetricCard = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  padding: 12px;
`;

const MetricLabel = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
`;

const MetricValue = styled.div`
  margin-top: 6px;
  font-family: 'Poppins', sans-serif;
  font-size: 26px;
  font-weight: 700;
  color: #0f172a;
`;

const ChartGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 1200px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const ChartPanel = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
  padding: 12px;
`;

const ChartTitle = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 10px;
`;

const ChartContainer = styled.div`
  height: 280px;
`;

const BarWrap = styled.div`
  height: 280px;
`;

const CategoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 10px;
`;

const CategoryButton = styled.button`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  color: #334155;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 8px;
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    border-color: #94a3b8;
    background: #f8fafc;
  }
`;

const StateText = styled.div`
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  background: #f8fafc;
  padding: 14px;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  color: #475569;
`;
